/**
 * Image compression utility for receipt uploads.
 * Resizes to a max dimension and re-encodes as JPEG to keep base64 payloads small.
 */
export async function compressImageToBase64(file, opts = {}) {
  const {
    maxDimension = 1280, // px (longest edge)
    quality = 0.75,      // JPEG quality 0..1
    mimeType = 'image/jpeg',
  } = opts;

  if (!file) return null;

  // If it's already a small file (<200 KB) and not HEIC, just read directly
  const dataUrl = await readFileAsDataURL(file);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDimension / Math.max(width, height));
      const targetW = Math.round(width * scale);
      const targetH = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetW, targetH);

      try {
        const compressed = canvas.toDataURL(mimeType, quality);
        resolve(compressed);
      } catch (e) {
        // Fall back to original if conversion failed (e.g. tainted canvas)
        resolve(dataUrl);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = dataUrl;
  });
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

/**
 * Approximate base64 payload size in bytes.
 */
export function base64ByteSize(dataUrl) {
  if (!dataUrl) return 0;
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  // base64 is 4 chars per 3 bytes; ignore padding for an approximation
  return Math.floor((base64.length * 3) / 4);
}
